import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Drawer, useDrawer } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { ChevronRight, Save, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApplicationAccountModel } from '@/common/models/applicationAccount'
import { useEditApplicationAccount } from '../hooks/useEditApplicationAccount'
import { useManageApplicationsAccount } from '../hooks/useManageApplicationsAccount'
import {
  ApplicationAccountForm,
  ApplicationAccountSchema,
} from '../schemas/ApplicationAccountSchema'
import { useSelectedAccountStore } from '../stores/useSelectedAccountStore'
import { FormEditApplicationAccount } from './FormEditApplicationAccount'
import { FormLinkApplicationAccount } from './FormLinkApplicationAccount'
import { LinkedApplicationAccount } from './LinkedApplicationAccount'

export function DrawerManageApplications() {
  const [applicationAccountToEdit, setApplicationAccountToEdit] =
    useState<ApplicationAccountModel | null>(null)

  const { t } = useTranslation('sso')
  const { ACCOUNT_SELECTED } = useManageApplicationsAccount()
  const { handleEditApplicationAccount, isEditingApplicationAccount } =
    useEditApplicationAccount()
  const drawer = useDrawer('drawer-manage-applications')
  const { selectAccount } = useSelectedAccountStore()
  const hookForm = useForm<ApplicationAccountForm>({
    resolver: zodResolver(ApplicationAccountSchema()),
    defaultValues: {
      applicationId: '',
      accountFields: undefined,
      tag: [],
      isMainAccount: false,
    },
  })

  const handleSubmitEdit = async () => {
    if (!applicationAccountToEdit) return

    await hookForm.handleSubmit(async (data) => {
      await handleEditApplicationAccount(applicationAccountToEdit, data).then(
        () => {
          setApplicationAccountToEdit(null)
        },
      )
    })()
  }

  const handleClose = () => {
    drawer.close()
    selectAccount(null)
  }

  return (
    <Drawer.Root id="drawer-manage-applications">
      <Drawer.Header onClose={handleClose}>
        <h1 className="text-lg text-brand-800 font-semibold flex items-center justify-center gap-2">
          {t('accounts.drawer_manage_applications.header.title')}
          <ChevronRight size={14} className="text-brand-800" />
          {ACCOUNT_SELECTED?.title}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {applicationAccountToEdit
            ? `Editar Aplicação`
            : t('accounts.drawer_manage_applications.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          {applicationAccountToEdit && (
            <FormEditApplicationAccount
              applicationAccount={applicationAccountToEdit}
              onEdit={() => setApplicationAccountToEdit(null)}
            />
          )}
        </FormProvider>

        {!applicationAccountToEdit && <FormLinkApplicationAccount />}

        {!applicationAccountToEdit && (
          <>
            <h2 className="text-2xl font-semibold text-brand-700">
              {t('accounts.drawer_manage_applications.body.subtitle')}
            </h2>

            <div className="space-y-4">
              {ACCOUNT_SELECTED?.ApplicationAccount.length === 0 && (
                <p className="text-sm text-gray-500">
                  {t(
                    'accounts.drawer_manage_applications.body.no_linked_applications',
                  )}
                </p>
              )}
              {ACCOUNT_SELECTED?.ApplicationAccount.map((linkedApplication) => (
                <LinkedApplicationAccount
                  key={linkedApplication.id}
                  application={linkedApplication}
                  onEdit={() => setApplicationAccountToEdit(linkedApplication)}
                />
              ))}
            </div>
          </>
        )}
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-end gap-4">
          {applicationAccountToEdit ? (
            <>
              <Button.Root
                variant="secondary"
                onClick={() => setApplicationAccountToEdit(null)}
              >
                <Button.Icon icon={X} />
                {t(
                  'accounts.drawer_manage_applications.footer.button_cancel.label',
                )}
              </Button.Root>

              <Button.Root
                type="submit"
                onClick={handleSubmitEdit}
                isLoading={isEditingApplicationAccount}
              >
                <Button.Icon icon={Save} />
                {t(
                  'accounts.drawer_manage_applications.footer.button_edit.label',
                )}
              </Button.Root>
            </>
          ) : (
            <Button.Root
              variant="secondary"
              color="brand"
              onClick={handleClose}
            >
              {t(
                'accounts.drawer_manage_applications.footer.button_close.label',
              )}
            </Button.Root>
          )}
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
